import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Archive, Trash2, List, Settings as Cog, X, Send, MessageSquare, ImagePlus, Clock, AlertTriangle, CheckCircle2, Loader2, ChevronLeft, ChevronRight, Camera, Star, UserPlus, FolderInput, CheckSquare, Square } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminCard from "./AdminCard";
import ReportCategoriesAdmin from "./ReportCategoriesAdmin";

const STATUS = {
    new: { label: "Nowe", cls: "bg-red-100 text-red-800", icon: AlertTriangle },
    in_progress: { label: "W trakcie", cls: "bg-amber-100 text-amber-800", icon: Loader2 },
    resolved: { label: "Rozwiązane", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
    archived: { label: "Archiwum", cls: "bg-gray-200 text-gray-700", icon: Archive },
};

const PRIORITY = {
    low: { label: "Niski", cls: "bg-gray-200 text-gray-700" },
    normal: { label: "Normalny", cls: "bg-blue-100 text-blue-700" },
    high: { label: "Wysoki", cls: "bg-orange-100 text-orange-800" },
    critical: { label: "⚠ Krytyczny", cls: "bg-red-600 text-white" },
};

const PAGE_SIZE = 20;

function fmtDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
}

function isOverdue(report, categories) {
    if (!report || report.status === "resolved" || report.status === "archived") return false;
    const cat = categories.find((c) => c.key === report.category);
    const slaH = cat?.sla_hours;
    if (!slaH || !report.created_at) return false;
    const createdMs = new Date(report.created_at).getTime();
    return Date.now() - createdMs > slaH * 3600 * 1000;
}

function ReportDetailModal({ report, onClose, onUpdated, canManage, meta }) {
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [closingNote, setClosingNote] = useState("");
    const [showResolveConfirm, setShowResolveConfirm] = useState(false);
    const fileInput = useRef(null);

    const refreshDetails = useCallback(async () => {
        try {
            const { data } = await api.get(`/reports/${report.id}`);
            onUpdated(data);
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    }, [report.id, onUpdated]);

    const changeStatus = async (newStatus) => {
        try {
            if (newStatus === "resolved" && !showResolveConfirm) {
                setShowResolveConfirm(true);
                return;
            }
            // When resolving: optionally push closing note before status change
            if (newStatus === "resolved" && closingNote.trim()) {
                await api.post(`/reports/${report.id}/notes`, { text: closingNote.trim() });
            }
            const { data } = await api.patch(`/reports/${report.id}?status=${newStatus}`);
            if (newStatus === "resolved") {
                toast.success(data.resolution_email_sent
                    ? "Oznaczono jako zrealizowane. E-mail do zgłaszającego został wysłany."
                    : "Oznaczono jako zrealizowane. (E-mail nie wysłany – sprawdź konfigurację e-mail w Ustawieniach.)");
            } else {
                toast.success("Zaktualizowano status.");
            }
            setShowResolveConfirm(false);
            setClosingNote("");
            await refreshDetails();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        setSending(true);
        try {
            await api.post(`/reports/${report.id}/notes`, { text: note.trim() });
            setNote("");
            await refreshDetails();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setSending(false); }
    };

    const deleteNote = async (nid) => {
        if (!window.confirm("Usunąć tę notatkę?")) return;
        try {
            await api.delete(`/reports/${report.id}/notes/${nid}`);
            await refreshDetails();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const uploadProof = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Wybierz plik graficzny."); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: upload } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.post(`/reports/${report.id}/proof-images`, { url: upload.url });
            toast.success("Zdjęcie dodane.");
            await refreshDetails();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setUploading(false); e.target.value = ""; }
    };

    const deleteProof = async (url) => {
        if (!window.confirm("Usunąć to zdjęcie?")) return;
        try {
            await api.delete(`/reports/${report.id}/proof-images`, { params: { url } });
            await refreshDetails();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const status = report.status || "new";
    const badge = STATUS[status] || STATUS.new;
    const overdue = isOverdue(report, meta.categories || []);
    const notes = report.worker_notes || [];
    const proofs = report.proof_images || [];
    const history = report.status_history || [];

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-3 md:p-6 overflow-y-auto" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full my-4" onClick={(e) => e.stopPropagation()} data-testid="report-detail-modal">
                {/* Header */}
                <div className="bg-[#092E22] text-white px-6 py-5 rounded-t-3xl flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            {report.ticket_number && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#D4A537] text-[#092E22] text-[11px] font-mono font-bold tracking-wider">
                                    {report.ticket_number}
                                </span>
                            )}
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${badge.cls}`}>
                                {badge.label}
                            </span>
                            {report.priority && PRIORITY[report.priority] && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${PRIORITY[report.priority].cls}`}>
                                    {PRIORITY[report.priority].label}
                                </span>
                            )}
                            {overdue && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold bg-red-500 text-white">
                                    ⏱ Przeterminowane
                                </span>
                            )}
                            {report.rating && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900">
                                    <Star size={10} className="fill-current" /> {report.rating}/5
                                </span>
                            )}
                        </div>
                        <h3 className="font-serif text-xl truncate">{report.name}</h3>
                        <p className="text-xs text-white/60 mt-0.5">
                            {report.category_label || report.category} • {fmtDate(report.created_at)}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 shrink-0" data-testid="report-detail-close">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Reporter info + description */}
                    <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div className="bg-[#FAF8F3] rounded-xl p-3">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">E-mail</p>
                            <a href={`mailto:${report.email}`} className="text-[#0D4F3C] break-all">{report.email}</a>
                        </div>
                        {report.phone && (
                            <div className="bg-[#FAF8F3] rounded-xl p-3">
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Telefon</p>
                                <a href={`tel:${report.phone}`} className="text-[#0D4F3C]">{report.phone}</a>
                            </div>
                        )}
                        {report.location && (
                            <div className="bg-[#FAF8F3] rounded-xl p-3 md:col-span-2">
                                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Lokalizacja</p>
                                <p className="text-[#092E22]">{report.location}</p>
                            </div>
                        )}
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Opis zgłoszenia</p>
                        <p className="text-sm text-gray-800 whitespace-pre-line bg-[#FAF8F3] rounded-xl p-4">{report.description}</p>
                    </div>

                    {/* Reporter photos */}
                    {(report.images || []).length > 0 && (
                        <div className="border-t border-gray-100 pt-4">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Zdjęcia od zgłaszającego ({report.images.length})</p>
                            <div className="flex flex-wrap gap-2" data-testid="report-reporter-images">
                                {report.images.map((u, i) => (
                                    <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 hover:border-[#0D4F3C]">
                                        <img src={u} alt={`Zdjęcie ${i + 1}`} className="w-full h-full object-cover" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Rating display */}
                    {report.rating && (
                        <div className="border-t border-gray-100 pt-4">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Ocena od zgłaszającego</p>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="report-rating">
                                <div className="flex items-center gap-1 mb-2">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star key={n} size={16} className={n <= report.rating ? "fill-[#D4A537] text-[#D4A537]" : "text-gray-200"} />
                                    ))}
                                    <span className="ml-2 text-sm font-semibold text-amber-900">{report.rating}/5</span>
                                    {report.rated_at && <span className="text-[10px] text-gray-400 ml-2">{fmtDate(report.rated_at)}</span>}
                                </div>
                                {report.rating_comment && (
                                    <p className="text-sm text-gray-700 italic whitespace-pre-line">„{report.rating_comment}"</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Status actions */}
                    {status !== "archived" && (
                        <div className="border-t border-gray-100 pt-4">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Zmień status</p>
                            {!showResolveConfirm ? (
                                <div className="flex flex-wrap gap-2" data-testid="report-detail-actions">
                                    {status === "new" && (
                                        <button onClick={() => changeStatus("in_progress")} data-testid="report-detail-start" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600">
                                            <Loader2 size={12} /> Rozpocznij realizację
                                        </button>
                                    )}
                                    {status !== "resolved" && (
                                        <button onClick={() => changeStatus("resolved")} data-testid="report-detail-resolve" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                                            <CheckCircle2 size={12} /> Oznacz jako zrealizowane
                                        </button>
                                    )}
                                    {canManage && status !== "archived" && (
                                        <button onClick={() => changeStatus("archived")} data-testid="report-detail-archive" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-500 text-white text-xs font-semibold hover:bg-gray-600">
                                            <Archive size={12} /> Archiwizuj
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3" data-testid="report-resolve-confirm">
                                    <p className="text-sm text-emerald-900 font-semibold">Oznaczyć jako zrealizowane?</p>
                                    <p className="text-xs text-emerald-800">Do zgłaszającego ({report.email}) zostanie wysłany e-mail z informacją, że usterka została usunięta. Możesz dołączyć komentarz:</p>
                                    <textarea
                                        value={closingNote}
                                        onChange={(e) => setClosingNote(e.target.value)}
                                        rows={3}
                                        placeholder="np. Zepsuty bezpiecznik wymieniony, oświetlenie działa poprawnie."
                                        className="w-full rounded-xl border border-emerald-300 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                        data-testid="report-resolve-note"
                                    />
                                    <div className="flex gap-2 justify-end">
                                        <button onClick={() => { setShowResolveConfirm(false); setClosingNote(""); }} className="px-3 py-1.5 rounded-full text-xs text-emerald-800 hover:bg-emerald-100">Anuluj</button>
                                        <button onClick={() => changeStatus("resolved")} data-testid="report-resolve-confirm-btn" className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                                            <Send size={12} /> Potwierdź i wyślij e-mail
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Proof images */}
                    <div className="border-t border-gray-100 pt-4">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Zdjęcia realizacji ({proofs.length})</p>
                            <button
                                onClick={() => fileInput.current?.click()}
                                disabled={uploading}
                                data-testid="report-proof-upload"
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0D4F3C] text-white text-[11px] font-semibold hover:bg-[#092E22] disabled:opacity-50"
                            >
                                <Camera size={11} /> {uploading ? "Wysyłanie..." : "Dodaj zdjęcie"}
                            </button>
                            <input ref={fileInput} type="file" accept="image/*" onChange={uploadProof} className="hidden" />
                        </div>
                        {proofs.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">Brak zdjęć. Dodaj zdjęcie przed i po realizacji — zgłaszający otrzyma je w mailu końcowym.</p>
                        ) : (
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                {proofs.map((u, i) => (
                                    <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200" data-testid={`report-proof-${i}`}>
                                        <img src={u} alt={`Zdjęcie ${i + 1}`} className="w-full h-full object-cover" />
                                        <button onClick={() => deleteProof(u)} className="absolute top-1 right-1 p-1 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity" title="Usuń">
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    <div className="border-t border-gray-100 pt-4">
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Notatki ({notes.length})</p>
                        <div className="space-y-2 mb-3 max-h-60 overflow-y-auto" data-testid="report-notes-list">
                            {notes.length === 0 && <p className="text-xs text-gray-400 italic">Brak notatek.</p>}
                            {notes.map((n) => {
                                const role = n.author_role;
                                const roleColor = role === "admin" ? "bg-amber-50 border-amber-100"
                                    : role === "reporter" ? "bg-rose-50 border-rose-200"
                                    : "bg-blue-50 border-blue-100";
                                const badgeColor = role === "admin" ? "bg-amber-200 text-amber-900"
                                    : role === "reporter" ? "bg-rose-200 text-rose-900"
                                    : "bg-blue-200 text-blue-900";
                                const roleLabel = role === "admin" ? "admin" : role === "reporter" ? "zgłaszający" : "wykonawca";
                                return (
                                <div key={n.id} className={`p-3 rounded-xl text-sm border ${roleColor}`} data-testid={`report-note-${n.id}`}>
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-xs font-semibold text-[#092E22]">{n.author_name || n.author_email}</span>
                                            <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full ${badgeColor}`}>
                                                {roleLabel}
                                            </span>
                                            <span className="text-[10px] text-gray-400">{fmtDate(n.created_at)}</span>
                                        </div>
                                        {role !== "reporter" && (
                                            <button onClick={() => deleteNote(n.id)} className="text-red-400 hover:text-red-600 opacity-50 hover:opacity-100" data-testid={`report-note-delete-${n.id}`} title="Usuń">
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-gray-800 whitespace-pre-line">{n.text}</p>
                                </div>
                                );
                            })}
                        </div>
                        <div className="flex gap-2" data-testid="report-add-note">
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={2}
                                placeholder="Dodaj notatkę (np. zrobione, zepsute było to i to)..."
                                className="flex-1 rounded-xl border border-gray-200 bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D4F3C]/30"
                                data-testid="report-note-input"
                            />
                            <button onClick={addNote} disabled={sending || !note.trim()} data-testid="report-note-send" className="inline-flex items-center gap-1.5 px-4 self-stretch rounded-xl bg-[#0D4F3C] text-white text-sm font-semibold hover:bg-[#092E22] disabled:opacity-50 disabled:cursor-not-allowed">
                                <MessageSquare size={14} /> Dodaj
                            </button>
                        </div>
                    </div>

                    {/* History */}
                    {history.length > 0 && (
                        <div className="border-t border-gray-100 pt-4">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Historia zmian ({history.length})</p>
                            <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="report-history">
                                {history.map((h, i) => {
                                    const b = STATUS[h.status] || { label: h.status, cls: "bg-gray-200" };
                                    return (
                                        <li key={i} className="flex items-center gap-2 text-xs">
                                            <Clock size={11} className="text-gray-400 shrink-0" />
                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${b.cls}`}>{b.label}</span>
                                            <span className="text-gray-500">{fmtDate(h.at)}</span>
                                            <span className="text-gray-400">—</span>
                                            <span className="text-gray-700 truncate">{h.by_name || "System"}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {/* Email diagnostics (admin only) */}
                    {canManage && (report.email_error || report.email_result?.failed > 0) && (
                        <div className="border-t border-gray-100 pt-4">
                            <p className="text-[10px] uppercase tracking-wider text-red-600 font-semibold mb-2">Problemy z e-mailami</p>
                            {report.email_result?.failed > 0 && (
                                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-800 mb-2">
                                    <p className="font-semibold mb-1">E-mail nie wysłany do {report.email_result.failed} odbiorców:</p>
                                    <ul className="list-disc pl-5 space-y-0.5 font-mono break-words">
                                        {(report.email_result.errors || []).map((e, i) => <li key={i}>{e}</li>)}
                                    </ul>
                                </div>
                            )}
                            {report.email_error && (
                                <code className="block p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-800 font-mono break-words">{report.email_error}</code>
                            )}
                        </div>
                    )}

                    {report.completion_notified_at && (
                        <div className="border-t border-gray-100 pt-4">
                            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-xl p-3">
                                <CheckCircle2 size={14} />
                                <span>E-mail z informacją o realizacji wysłany do zgłaszającego: <strong>{fmtDate(report.completion_notified_at)}</strong></span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function ZgloszeniaAdmin({ onUpdate }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState("new");
    const [category, setCategory] = useState("");
    const [view, setView] = useState("list"); // list | categories
    const [meta, setMeta] = useState({ can_manage: true, can_handle: false, assigned_categories: [], categories: [] });
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkAction, setBulkAction] = useState(null); // { type, label }
    const [bulkCategory, setBulkCategory] = useState("");
    const [bulkWorker, setBulkWorker] = useState("");
    const [workers, setWorkers] = useState([]);
    const [bulkLoading, setBulkLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (status) params.set("status", status);
            if (category) params.set("category", category);
            params.set("page", String(page));
            params.set("limit", String(PAGE_SIZE));
            const { data } = await api.get(`/reports?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
            setPages(data.pages || 1);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    }, [status, category, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/reports-meta").then((r) => setMeta(r.data || {})).catch(() => {});
        // Load workers (moderators with handle_assigned_reports) for bulk assign
        api.get("/users").then((r) => {
            const list = (r.data || []).filter((u) => u.role !== "admin" && u.permissions?.handle_assigned_reports);
            setWorkers(list);
        }).catch(() => {});
    }, []);

    // Clear selection when filters change
    useEffect(() => { setSelectedIds(new Set()); }, [status, category, page]);

    const toggleOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAllOnPage = () => {
        const allIds = items.map((r) => r.id);
        const allSelected = allIds.every((id) => selectedIds.has(id));
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allSelected) allIds.forEach((id) => next.delete(id));
            else allIds.forEach((id) => next.add(id));
            return next;
        });
    };
    const clearSelection = () => setSelectedIds(new Set());

    const runBulk = async () => {
        if (!bulkAction || selectedIds.size === 0) return;
        const payload = { ids: Array.from(selectedIds), action: bulkAction.type };
        if (bulkAction.type === "change_category") {
            if (!bulkCategory) { toast.error("Wybierz kategorię."); return; }
            payload.category = bulkCategory;
        }
        if (bulkAction.type === "assign_worker") {
            if (!bulkWorker) { toast.error("Wybierz wykonawcę."); return; }
            payload.worker_email = bulkWorker;
        }
        setBulkLoading(true);
        try {
            const { data } = await api.post("/reports/bulk", payload);
            const n = data.updated ?? data.deleted ?? 0;
            toast.success(`Akcja wykonana na ${n} zgłoszeniach.`);
            setBulkAction(null);
            setBulkCategory("");
            setBulkWorker("");
            clearSelection();
            await load();
            onUpdate?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Nie udało się wykonać akcji.");
        } finally { setBulkLoading(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Usunąć zgłoszenie?")) return;
        try {
            await api.delete(`/reports/${id}`);
            await load();
            onUpdate?.();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    const canManage = !!meta.can_manage;

    return (
        <div className="space-y-4">
            {canManage && (
                <div className="flex flex-wrap gap-2" data-testid="zgloszenia-subtabs">
                    <button onClick={() => setView("list")} className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "list" ? "bg-[#0D4F3C] text-white" : "bg-white text-gray-600 border border-gray-200 hover:text-[#0D4F3C]"}`} data-testid="zgloszenia-subtab-list">
                        <List size={12} /> Lista zgłoszeń
                    </button>
                    <button onClick={() => setView("categories")} className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "categories" ? "bg-[#0D4F3C] text-white" : "bg-white text-gray-600 border border-gray-200 hover:text-[#0D4F3C]"}`} data-testid="zgloszenia-subtab-categories">
                        <Cog size={12} /> Kategorie &amp; e-maile
                    </button>
                </div>
            )}

            {view === "categories" ? (
                <ReportCategoriesAdmin />
            ) : (
                <AdminCard>
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                        <div>
                            <h3 className="font-serif text-xl text-[#092E22]">
                                {canManage ? "Zgłoszenia usterek" : "Moje zgłoszenia do realizacji"}
                            </h3>
                            {!canManage && meta.assigned_categories?.length > 0 && (
                                <p className="text-[11px] text-gray-500 mt-1">
                                    Twoje kategorie: {meta.assigned_categories.join(", ")}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                        <div className="flex gap-1 bg-[#FAF8F3] rounded-full p-1" data-testid="admin-reports-filter">
                            {["new", "in_progress", "resolved", "archived", "all"].map((k) => (
                                <button
                                    key={k}
                                    onClick={() => { setStatus(k); setPage(1); }}
                                    data-testid={`admin-reports-filter-${k}`}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${status === k ? "bg-[#0D4F3C] text-white" : "text-gray-600 hover:text-[#0D4F3C]"}`}
                                >
                                    {k === "new" ? "Nowe" : k === "in_progress" ? "W trakcie" : k === "resolved" ? "Rozwiązane" : k === "archived" ? "Archiwum" : "Wszystkie"}
                                </button>
                            ))}
                        </div>
                        {meta.categories?.length > 1 && (
                            <select
                                value={category}
                                onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                                className="h-8 rounded-full border border-gray-200 bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#0D4F3C]/30"
                                data-testid="admin-reports-category-filter"
                            >
                                <option value="">Wszystkie kategorie</option>
                                {meta.categories.map((c) => (
                                    <option key={c.key} value={c.key}>{c.label}</option>
                                ))}
                            </select>
                        )}
                        <span className="text-xs text-gray-500 ml-auto">{loading ? "Wczytywanie..." : `${total} zgłoszeń`}</span>
                    </div>

                    {/* Select all + bulk toolbar (admin only) */}
                    {canManage && items.length > 0 && (
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                            <button
                                onClick={toggleAllOnPage}
                                data-testid="admin-reports-select-all"
                                className="inline-flex items-center gap-2 text-xs text-gray-600 hover:text-[#0D4F3C]"
                            >
                                {items.every((r) => selectedIds.has(r.id)) ? <CheckSquare size={14} /> : <Square size={14} />}
                                {items.every((r) => selectedIds.has(r.id)) ? "Odznacz wszystkie" : "Zaznacz wszystkie na stronie"}
                            </button>
                            {selectedIds.size > 0 && (
                                <span className="text-xs text-[#0D4F3C] font-semibold" data-testid="admin-reports-selected-count">
                                    {selectedIds.size} zaznaczonych
                                </span>
                            )}
                        </div>
                    )}

                    {/* List */}
                    <div className="space-y-2.5">
                        {items.length === 0 && !loading && <p className="text-gray-500 text-center py-8">Brak zgłoszeń w tym widoku.</p>}
                        {items.map((r) => {
                            const st = r.status || "new";
                            const badge = STATUS[st];
                            const overdue = isOverdue(r, meta.categories || []);
                            const notesCount = (r.worker_notes || []).length;
                            const proofsCount = (r.proof_images || []).length;
                            const isSelected = selectedIds.has(r.id);
                            return (
                                <div
                                    key={r.id}
                                    className={`flex items-start gap-2 p-3 rounded-2xl border transition-all ${isSelected ? "bg-[#0D4F3C]/5 border-[#0D4F3C]/40" : "bg-[#FAF8F3] border-gray-100 hover:border-[#0D4F3C]/40"}`}
                                    data-testid={`admin-report-row-${r.id}`}
                                >
                                    {canManage && (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); toggleOne(r.id); }}
                                            data-testid={`admin-report-select-${r.id}`}
                                            className="shrink-0 mt-1 w-5 h-5 rounded border-2 border-gray-300 hover:border-[#0D4F3C] flex items-center justify-center bg-white transition-colors"
                                            title="Zaznacz"
                                        >
                                            {isSelected && <CheckSquare size={14} className="text-[#0D4F3C]" />}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setSelected(r)}
                                        className="flex-1 min-w-0 text-left hover:bg-white rounded-xl p-1 -m-1 transition-colors"
                                        data-testid={`admin-report-${r.id}`}
                                    >
                                    <div className="flex items-start gap-3 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                {r.ticket_number && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#0D4F3C] text-white text-[10px] font-mono font-semibold tracking-wider">
                                                        {r.ticket_number}
                                                    </span>
                                                )}
                                                <p className="font-semibold text-[#092E22] text-sm truncate">{r.name}</p>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${badge.cls}`}>{badge.label}</span>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#D4A537]/20 text-[#B88A26]">
                                                    {r.category_label || r.category}
                                                </span>
                                                {r.priority && PRIORITY[r.priority] && r.priority !== "normal" && (
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${PRIORITY[r.priority].cls}`}>
                                                        {PRIORITY[r.priority].label}
                                                    </span>
                                                )}
                                                {(r.images || []).length > 0 && (
                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] bg-purple-100 text-purple-800" title={`${r.images.length} zdjęć od zgłaszającego`}>
                                                        📷 {r.images.length}
                                                    </span>
                                                )}
                                                {r.rating && (
                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-900">
                                                        <Star size={9} className="fill-current" /> {r.rating}
                                                    </span>
                                                )}
                                                {overdue && (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500 text-white">
                                                        ⏱ Przeterminowane
                                                    </span>
                                                )}
                                                {notesCount > 0 && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-800">
                                                        <MessageSquare size={9} /> {notesCount}
                                                    </span>
                                                )}
                                                {proofsCount > 0 && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-800">
                                                        <ImagePlus size={9} /> {proofsCount}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-600 line-clamp-2">{r.description}</p>
                                            <p className="text-[11px] text-gray-400 mt-1">
                                                {fmtDate(r.created_at)}
                                                {r.email && ` • ${r.email}`}
                                                {r.location && ` • ${r.location}`}
                                            </p>
                                        </div>
                                    </div>
                                    </button>
                                    {canManage && (
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => { e.stopPropagation(); del(r.id); }}
                                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); del(r.id); } }}
                                            data-testid={`admin-report-delete-${r.id}`}
                                            className="text-red-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 shrink-0 inline-flex items-center justify-center cursor-pointer mt-1"
                                            title="Usuń zgłoszenie"
                                        >
                                            <Trash2 size={14} />
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    {pages > 1 && (
                        <div className="flex items-center justify-between pt-5 mt-4 border-t border-gray-100" data-testid="admin-reports-pagination">
                            <p className="text-xs text-gray-500">Strona {page} z {pages}</p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-xs disabled:opacity-50 hover:border-[#0D4F3C]"
                                    data-testid="admin-reports-prev"
                                >
                                    <ChevronLeft size={14} /> Poprzednia
                                </button>
                                <button
                                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                                    disabled={page >= pages}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-xs disabled:opacity-50 hover:border-[#0D4F3C]"
                                    data-testid="admin-reports-next"
                                >
                                    Następna <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>
                    )}
                </AdminCard>
            )}

            {selected && (
                <ReportDetailModal
                    report={selected}
                    canManage={canManage}
                    meta={meta}
                    onClose={() => { setSelected(null); load(); onUpdate?.(); }}
                    onUpdated={(fresh) => setSelected(fresh)}
                />
            )}

            {/* Floating bulk action bar */}
            {canManage && selectedIds.size > 0 && !bulkAction && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-[#092E22] text-white rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-2 flex-wrap max-w-[94vw]" data-testid="admin-reports-bulk-bar">
                    <span className="text-sm font-semibold mr-2">
                        {selectedIds.size} zaznaczonych
                    </span>
                    <button
                        onClick={() => setBulkAction({ type: "resolve", label: "oznaczyć jako rozwiązane" })}
                        data-testid="admin-reports-bulk-resolve"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-xs font-semibold"
                    >
                        <CheckCircle2 size={12} /> Rozwiąż
                    </button>
                    <button
                        onClick={() => setBulkAction({ type: "archive", label: "zarchiwizować" })}
                        data-testid="admin-reports-bulk-archive"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-500 hover:bg-gray-600 text-xs font-semibold"
                    >
                        <Archive size={12} /> Archiwizuj
                    </button>
                    <button
                        onClick={() => setBulkAction({ type: "assign_worker", label: "przypisać wykonawcę" })}
                        data-testid="admin-reports-bulk-assign"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-500 hover:bg-indigo-600 text-xs font-semibold"
                    >
                        <UserPlus size={12} /> Przypisz
                    </button>
                    <button
                        onClick={() => setBulkAction({ type: "change_category", label: "zmienić kategorię" })}
                        data-testid="admin-reports-bulk-category"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-xs font-semibold"
                    >
                        <FolderInput size={12} /> Kategoria
                    </button>
                    <button
                        onClick={() => setBulkAction({ type: "delete", label: "USUNĄĆ NA ZAWSZE" })}
                        data-testid="admin-reports-bulk-delete"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-xs font-semibold"
                    >
                        <Trash2 size={12} /> Usuń
                    </button>
                    <button
                        onClick={clearSelection}
                        data-testid="admin-reports-bulk-clear"
                        className="ml-1 p-1.5 rounded-full hover:bg-white/10"
                        title="Wyczyść zaznaczenie"
                    >
                        <X size={14} />
                    </button>
                </div>
            )}

            {/* Bulk confirmation modal */}
            {bulkAction && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !bulkLoading && setBulkAction(null)}>
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-reports-bulk-modal">
                        <h3 className="font-serif text-xl text-[#092E22] mb-2">
                            {bulkAction.type === "delete" ? "⚠ Usunąć" : "Wykonać akcję"} na {selectedIds.size} zgłoszeniach?
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {bulkAction.type === "delete"
                                ? "Ta operacja jest nieodwracalna. Wszystkie zaznaczone zgłoszenia, notatki i zdjęcia zostaną skasowane."
                                : `Chcesz ${bulkAction.label} dla ${selectedIds.size} zaznaczonych zgłoszeń.`}
                        </p>

                        {bulkAction.type === "change_category" && (
                            <div className="mb-4">
                                <label className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Nowa kategoria</label>
                                <select
                                    value={bulkCategory}
                                    onChange={(e) => setBulkCategory(e.target.value)}
                                    className="mt-1 w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D4F3C]/30"
                                    data-testid="admin-reports-bulk-category-select"
                                >
                                    <option value="">— wybierz —</option>
                                    {(meta.categories || []).map((c) => (
                                        <option key={c.key} value={c.key}>{c.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {bulkAction.type === "assign_worker" && (
                            <div className="mb-4">
                                <label className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Wykonawca</label>
                                <select
                                    value={bulkWorker}
                                    onChange={(e) => setBulkWorker(e.target.value)}
                                    className="mt-1 w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D4F3C]/30"
                                    data-testid="admin-reports-bulk-worker-select"
                                >
                                    <option value="">— wybierz wykonawcę —</option>
                                    {workers.map((w) => (
                                        <option key={w.id} value={w.email}>
                                            {w.name ? `${w.name} <${w.email}>` : w.email}
                                        </option>
                                    ))}
                                </select>
                                {workers.length === 0 && (
                                    <p className="text-[11px] text-amber-600 mt-1">Brak wykonawców. Dodaj w zakładce „Użytkownicy".</p>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setBulkAction(null)}
                                disabled={bulkLoading}
                                className="px-4 py-2 rounded-full text-sm text-gray-600 hover:bg-gray-100"
                            >
                                Anuluj
                            </button>
                            <button
                                onClick={runBulk}
                                disabled={bulkLoading}
                                data-testid="admin-reports-bulk-confirm"
                                className={`px-5 py-2 rounded-full text-sm font-semibold text-white ${bulkAction.type === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-[#0D4F3C] hover:bg-[#092E22]"} disabled:opacity-50`}
                            >
                                {bulkLoading ? "Wykonywanie..." : "Potwierdź"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
